import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import superUserDebugService from '@/services/superUserDebugService.ts';
import type {
  DebugSessionsQuery,
  DebugSessionStatus,
  DebugSessionsSort,
  PaginatedDebugSessionsResponse,
} from '@/types/superUserDebug.ts';

const DEFAULT_PAGE_SIZE = 25;
const ALLOWED_STATUSES: DebugSessionStatus[] = ['InProgress', 'Completed', 'Abandoned'];
const ALLOWED_SORTS: DebugSessionsSort[] = ['StartedAtDesc', 'StartedAtAsc'];

function parseQuery(params: URLSearchParams): DebugSessionsQuery {
  const pageRaw = Number(params.get('page'));
  const pageSizeRaw = Number(params.get('pageSize'));
  const status = params.get('status') as DebugSessionStatus | null;
  const sort = params.get('sort') as DebugSessionsSort | null;

  return {
    companyId: params.get('companyId') ?? undefined,
    salespersonId: params.get('salespersonId') ?? undefined,
    simulationId: params.get('simulationId') ?? undefined,
    dateFrom: params.get('dateFrom') ?? undefined,
    dateTo: params.get('dateTo') ?? undefined,
    status: status && ALLOWED_STATUSES.includes(status) ? status : undefined,
    q: params.get('q') ?? undefined,
    page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1,
    pageSize: Number.isFinite(pageSizeRaw) && pageSizeRaw > 0 ? pageSizeRaw : DEFAULT_PAGE_SIZE,
    sort: sort && ALLOWED_SORTS.includes(sort) ? sort : 'StartedAtDesc',
  };
}

function queryToSearchParams(query: DebugSessionsQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.companyId) params.set('companyId', query.companyId);
  if (query.salespersonId) params.set('salespersonId', query.salespersonId);
  if (query.simulationId) params.set('simulationId', query.simulationId);
  if (query.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query.dateTo) params.set('dateTo', query.dateTo);
  if (query.status) params.set('status', query.status);
  if (query.q) params.set('q', query.q);
  if (query.page && query.page !== 1) params.set('page', String(query.page));
  if (query.pageSize && query.pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(query.pageSize));
  if (query.sort && query.sort !== 'StartedAtDesc') params.set('sort', query.sort);
  return params;
}

export type DebugFilterKey =
  | 'companyId'
  | 'salespersonId'
  | 'simulationId'
  | 'dateFrom'
  | 'dateTo'
  | 'status'
  | 'q';

export interface UseSuperUserDebugSessionsResult {
  query: DebugSessionsQuery;
  data: PaginatedDebugSessionsResponse | null;
  isLoading: boolean;
  error: string | null;
  setFilter: (key: DebugFilterKey, value: string | undefined) => void;
  setPage: (page: number) => void;
  setSort: (sort: DebugSessionsSort) => void;
  reset: () => void;
  refresh: () => void;
}

export function useSuperUserDebugSessions(): UseSuperUserDebugSessionsResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseQuery(searchParams), [searchParams]);

  const [data, setData] = useState<PaginatedDebugSessionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    superUserDebugService
      .getSessions(query)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query.companyId,
    query.salespersonId,
    query.simulationId,
    query.dateFrom,
    query.dateTo,
    query.status,
    query.q,
    query.page,
    query.pageSize,
    query.sort,
    refreshTick,
  ]);

  const updateParams = useCallback(
    (next: DebugSessionsQuery) => {
      const params = queryToSearchParams(next);
      setSearchParams(params, { replace: false });
    },
    [setSearchParams],
  );

  const setFilter = useCallback(
    (key: DebugFilterKey, value: string | undefined) => {
      const next: DebugSessionsQuery = {
        ...query,
        [key]: value && value.length > 0 ? value : undefined,
        page: 1,
      };
      updateParams(next);
    },
    [query, updateParams],
  );

  const setPage = useCallback(
    (page: number) => {
      updateParams({ ...query, page });
    },
    [query, updateParams],
  );

  const setSort = useCallback(
    (sort: DebugSessionsSort) => {
      updateParams({ ...query, sort, page: 1 });
    },
    [query, updateParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: false });
  }, [setSearchParams]);

  const refresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  return { query, data, isLoading, error, setFilter, setPage, setSort, reset, refresh };
}
